import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';

// Types matching backend DTOs
export interface RoutingComparisonResult {
  routingExists?: boolean;
  routingUuid?: string;
  existingRoutingName?: string;
  importedRoutingName?: string;
  routingChanges?: Record<string, FieldChange>;
  operationDeltas?: OperationDelta[];
  relationshipDeltas?: RelationshipDelta[];
  resourceRequirementDeltas?: ResourceRequirementDelta[];
  summary?: ComparisonSummary;
}

export interface FieldChange {
  fieldName?: string;
  oldValue?: any;
  newValue?: any;
  changeType?: ChangeType;
}

export type ChangeType = 'UNCHANGED' | 'MODIFIED' | 'ADDED' | 'REMOVED';

export interface OperationDelta {
  operationUuid?: string;
  operationNumber?: number;
  operationName?: string;
  changeType?: ChangeType;
  fieldChanges?: Record<string, FieldChange>;
  existingOperation?: any;
  importedOperation?: any;
}

export interface RelationshipDelta {
  relationshipUuid?: string;
  predecessorName?: string;
  successorName?: string;
  changeType?: ChangeType;
  fieldChanges?: Record<string, FieldChange>;
  existingRelationship?: any;
  importedRelationship?: any;
}

export interface ResourceRequirementDelta {
  resourceRequirementUuid?: string;
  fromOperationName?: string;
  toOperationName?: string;
  changeType?: ChangeType;
  fieldChanges?: Record<string, FieldChange>;
  existingRequirement?: any;
  importedRequirement?: any;
}

export interface ComparisonSummary {
  totalChanges?: number;
  routingFieldChanges?: number;
  addedOperations?: number;
  removedOperations?: number;
  modifiedOperations?: number;
  unchangedOperations?: number;
  addedRelationships?: number;
  removedRelationships?: number;
  modifiedRelationships?: number;
  unchangedRelationships?: number;
  addedResourceRequirements?: number;
  removedResourceRequirements?: number;
  modifiedResourceRequirements?: number;
  unchangedResourceRequirements?: number;
}

interface RoutingDeltaViewProps {
  comparisonResult: RoutingComparisonResult;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const RoutingDeltaView: React.FC<RoutingDeltaViewProps> = ({
  comparisonResult,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation(['common']);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set([
    'routing',
    'operations',
    'relationships',
    'resourceRequirements',
  ]));
  const [showOnlyChanges, setShowOnlyChanges] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const getChangeTypeColor = (changeType?: ChangeType): string => {
    switch (changeType) {
      case 'ADDED':
        return '#22c55e'; // green-500
      case 'REMOVED':
        return '#ef4444'; // red-500
      case 'MODIFIED':
        return '#f59e0b'; // amber-500
      case 'UNCHANGED':
        return '#6b7280'; // gray-500
      default:
        return '#6b7280';
    }
  };

  const getChangeTypeIcon = (changeType?: ChangeType): string => {
    switch (changeType) {
      case 'ADDED':
        return '➕';
      case 'REMOVED':
        return '➖';
      case 'MODIFIED':
        return '✏️';
      case 'UNCHANGED':
        return '✓';
      default:
        return '';
    }
  };

  const getChangeTypeLabel = (changeType?: ChangeType): string => {
    switch (changeType) {
      case 'ADDED':
        return t('routing.import.delta.added', 'Neu');
      case 'REMOVED':
        return t('routing.import.delta.removed', 'Gelöscht');
      case 'MODIFIED':
        return t('routing.import.delta.modified', 'Geändert');
      case 'UNCHANGED':
        return t('routing.import.delta.unchanged', 'Unverändert');
      default:
        return '';
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? t('common.yes', 'Ja') : t('common.no', 'Nein');
    }
    return String(value);
  };

  // Filter operations based on showOnlyChanges
  const filteredOperationDeltas = useMemo(() => {
    if (!comparisonResult.operationDeltas) return [];
    if (!showOnlyChanges) return comparisonResult.operationDeltas;
    return comparisonResult.operationDeltas.filter(
      delta => delta.changeType !== 'UNCHANGED'
    );
  }, [comparisonResult.operationDeltas, showOnlyChanges]);

  // Filter relationships based on showOnlyChanges
  const filteredRelationshipDeltas = useMemo(() => {
    if (!comparisonResult.relationshipDeltas) return [];
    if (!showOnlyChanges) return comparisonResult.relationshipDeltas;
    return comparisonResult.relationshipDeltas.filter(
      delta => delta.changeType !== 'UNCHANGED'
    );
  }, [comparisonResult.relationshipDeltas, showOnlyChanges]);

  // Filter resource requirements based on showOnlyChanges
  const filteredResourceRequirementDeltas = useMemo(() => {
    if (!comparisonResult.resourceRequirementDeltas) return [];
    if (!showOnlyChanges) return comparisonResult.resourceRequirementDeltas;
    return comparisonResult.resourceRequirementDeltas.filter(
      delta => delta.changeType !== 'UNCHANGED'
    );
  }, [comparisonResult.resourceRequirementDeltas, showOnlyChanges]);

  const summary = comparisonResult.summary;

  return (
    <div className="routing-delta-view" style={{ padding: '16px' }}>
      {/* Summary Cards */}
      {summary && (
        <div className="mb-6 grid grid-cols-4 gap-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm text-amber-600 font-medium">
              {t('routing.import.delta.modified', 'Geändert')}
            </div>
            <div className="text-2xl font-bold text-amber-700">
              {(summary.modifiedOperations || 0) + (summary.modifiedRelationships || 0) + (summary.modifiedResourceRequirements || 0) + (summary.routingFieldChanges || 0)}
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">
              {t('routing.import.delta.added', 'Neu')}
            </div>
            <div className="text-2xl font-bold text-green-700">
              {(summary.addedOperations || 0) + (summary.addedRelationships || 0) + (summary.addedResourceRequirements || 0)}
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm text-red-600 font-medium">
              {t('routing.import.delta.removed', 'Gelöscht')}
            </div>
            <div className="text-2xl font-bold text-red-700">
              {(summary.removedOperations || 0) + (summary.removedRelationships || 0) + (summary.removedResourceRequirements || 0)}
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600 font-medium">
              {t('routing.import.delta.unchanged', 'Unverändert')}
            </div>
            <div className="text-2xl font-bold text-gray-700">
              {(summary.unchangedOperations || 0) + (summary.unchangedRelationships || 0) + (summary.unchangedResourceRequirements || 0)}
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="mb-4 flex items-center gap-2">
        <CheckBoxComponent
          checked={showOnlyChanges}
          change={(e: any) => setShowOnlyChanges(Boolean(e?.checked ?? false))}
        />
        <label className="text-sm font-medium text-gray-700">
          {t('routing.import.delta.showOnlyChanges', 'Nur Änderungen anzeigen')}
        </label>
      </div>

      {/* Routing Level Changes */}
      {comparisonResult.routingChanges && Object.keys(comparisonResult.routingChanges).length > 0 && (
        <div className="mb-4 border border-gray-300 rounded-lg">
          <div
            className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('routing')}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">
                {expandedSections.has('routing') ? '▼' : '▶'}
              </span>
              <span className="font-semibold text-lg">
                {t('routing.import.delta.routing', 'Routing')}: {comparisonResult.importedRoutingName || '-'}
              </span>
            </div>
            {summary && (summary.routingFieldChanges || 0) > 0 && (
              <span className="text-sm text-amber-600 font-medium">
                {summary.routingFieldChanges} {t('routing.import.delta.changes', 'Änderungen')}
              </span>
            )}
          </div>
          {expandedSections.has('routing') && (
            <div className="p-4 space-y-2">
              {Object.entries(comparisonResult.routingChanges).map(([fieldName, change]) => {
                if (showOnlyChanges && change.changeType === 'UNCHANGED') return null;
                return (
                  <div
                    key={fieldName}
                    className="flex items-center gap-4 p-2 rounded"
                    style={{
                      backgroundColor: change.changeType === 'UNCHANGED' ? 'transparent' : `${getChangeTypeColor(change.changeType)}15`,
                    }}
                  >
                    <span className="text-lg">{getChangeTypeIcon(change.changeType)}</span>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {t(`routing.fields.${fieldName}`, fieldName)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {change.changeType === 'ADDED' ? (
                          <span style={{ color: getChangeTypeColor(change.changeType) }}>
                            {formatValue(change.newValue)}
                          </span>
                        ) : change.changeType === 'REMOVED' ? (
                          <span style={{ color: getChangeTypeColor(change.changeType) }}>
                            {formatValue(change.oldValue)} → -
                          </span>
                        ) : change.changeType === 'MODIFIED' ? (
                          <span>
                            <span style={{ color: getChangeTypeColor(change.changeType) }}>
                              {formatValue(change.oldValue)}
                            </span>
                            {' → '}
                            <span style={{ color: getChangeTypeColor(change.changeType) }}>
                              {formatValue(change.newValue)}
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: getChangeTypeColor(change.changeType) }}>
                            {formatValue(change.newValue)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className="text-xs font-medium px-2 py-1 rounded"
                      style={{
                        backgroundColor: `${getChangeTypeColor(change.changeType)}20`,
                        color: getChangeTypeColor(change.changeType),
                      }}
                    >
                      {getChangeTypeLabel(change.changeType)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Operations */}
      {filteredOperationDeltas.length > 0 && (
        <div className="mb-4 border border-gray-300 rounded-lg">
          <div
            className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('operations')}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">
                {expandedSections.has('operations') ? '▼' : '▶'}
              </span>
              <span className="font-semibold text-lg">
                {t('routing.import.delta.operations', 'Operationen')} ({filteredOperationDeltas.length})
              </span>
            </div>
            {summary && (
              <div className="flex gap-2 text-sm">
                {(summary.addedOperations || 0) > 0 && (
                  <span className="text-green-600 font-medium">+{summary.addedOperations}</span>
                )}
                {(summary.removedOperations || 0) > 0 && (
                  <span className="text-red-600 font-medium">-{summary.removedOperations}</span>
                )}
                {(summary.modifiedOperations || 0) > 0 && (
                  <span className="text-amber-600 font-medium">~{summary.modifiedOperations}</span>
                )}
              </div>
            )}
          </div>
          {expandedSections.has('operations') && (
            <div className="p-4 space-y-3">
              {filteredOperationDeltas.map((delta, index) => (
                <div
                  key={delta.operationUuid || `op-${index}`}
                  className="border border-gray-200 rounded p-3"
                  style={{
                    backgroundColor: `${getChangeTypeColor(delta.changeType)}10`,
                    borderLeft: `4px solid ${getChangeTypeColor(delta.changeType)}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getChangeTypeIcon(delta.changeType)}</span>
                      <span className="font-semibold text-gray-900">
                        {delta.operationNumber != null ? `Operation ${delta.operationNumber}` : 'Operation'}: {delta.operationName || '-'}
                      </span>
                    </div>
                    <span
                      className="text-xs font-medium px-2 py-1 rounded"
                      style={{
                        backgroundColor: `${getChangeTypeColor(delta.changeType)}20`,
                        color: getChangeTypeColor(delta.changeType),
                      }}
                    >
                      {getChangeTypeLabel(delta.changeType)}
                    </span>
                  </div>
                  {delta.fieldChanges && Object.keys(delta.fieldChanges).length > 0 && (
                    <div className="ml-8 mt-2 space-y-1">
                      {Object.entries(delta.fieldChanges).map(([fieldName, change]) => {
                        if (showOnlyChanges && change.changeType === 'UNCHANGED') return null;
                        return (
                          <div key={fieldName} className="text-sm">
                            <span className="font-medium text-gray-700">
                              {t(`operation.fields.${fieldName}`, fieldName)}:
                            </span>{' '}
                            {change.changeType === 'MODIFIED' ? (
                              <span>
                                <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                  {formatValue(change.oldValue)}
                                </span>
                                {' → '}
                                <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                  {formatValue(change.newValue)}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                {formatValue(change.newValue || change.oldValue)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Relationships */}
      {filteredRelationshipDeltas.length > 0 && (
        <div className="mb-4 border border-gray-300 rounded-lg">
          <div
            className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('relationships')}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">
                {expandedSections.has('relationships') ? '▼' : '▶'}
              </span>
              <span className="font-semibold text-lg">
                {t('routing.import.delta.relationships', 'Beziehungen')} ({filteredRelationshipDeltas.length})
              </span>
            </div>
            {summary && (
              <div className="flex gap-2 text-sm">
                {(summary.addedRelationships || 0) > 0 && (
                  <span className="text-green-600 font-medium">+{summary.addedRelationships}</span>
                )}
                {(summary.removedRelationships || 0) > 0 && (
                  <span className="text-red-600 font-medium">-{summary.removedRelationships}</span>
                )}
                {(summary.modifiedRelationships || 0) > 0 && (
                  <span className="text-amber-600 font-medium">~{summary.modifiedRelationships}</span>
                )}
              </div>
            )}
          </div>
          {expandedSections.has('relationships') && (
            <div className="p-4 space-y-3">
              {filteredRelationshipDeltas.map((delta, index) => (
                <div
                  key={delta.relationshipUuid || `rel-${index}`}
                  className="border border-gray-200 rounded p-3"
                  style={{
                    backgroundColor: `${getChangeTypeColor(delta.changeType)}10`,
                    borderLeft: `4px solid ${getChangeTypeColor(delta.changeType)}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getChangeTypeIcon(delta.changeType)}</span>
                      <span className="font-semibold text-gray-900">
                        {delta.predecessorName || '-'} → {delta.successorName || '-'}
                      </span>
                    </div>
                    <span
                      className="text-xs font-medium px-2 py-1 rounded"
                      style={{
                        backgroundColor: `${getChangeTypeColor(delta.changeType)}20`,
                        color: getChangeTypeColor(delta.changeType),
                      }}
                    >
                      {getChangeTypeLabel(delta.changeType)}
                    </span>
                  </div>
                  {delta.fieldChanges && Object.keys(delta.fieldChanges).length > 0 && (
                    <div className="ml-8 mt-2 space-y-1">
                      {Object.entries(delta.fieldChanges).map(([fieldName, change]) => {
                        if (showOnlyChanges && change.changeType === 'UNCHANGED') return null;
                        return (
                          <div key={fieldName} className="text-sm">
                            <span className="font-medium text-gray-700">
                              {t(`relationship.fields.${fieldName}`, fieldName)}:
                            </span>{' '}
                            {change.changeType === 'MODIFIED' ? (
                              <span>
                                <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                  {formatValue(change.oldValue)}
                                </span>
                                {' → '}
                                <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                  {formatValue(change.newValue)}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                {formatValue(change.newValue || change.oldValue)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Resource Requirements */}
      {filteredResourceRequirementDeltas.length > 0 && (
        <div className="mb-4 border border-gray-300 rounded-lg">
          <div
            className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('resourceRequirements')}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">
                {expandedSections.has('resourceRequirements') ? '▼' : '▶'}
              </span>
              <span className="font-semibold text-lg">
                {t('routing.import.delta.resourceRequirements', 'Ressourcenanforderungen')} ({filteredResourceRequirementDeltas.length})
              </span>
            </div>
            {summary && (
              <div className="flex gap-2 text-sm">
                {(summary.addedResourceRequirements || 0) > 0 && (
                  <span className="text-green-600 font-medium">+{summary.addedResourceRequirements}</span>
                )}
                {(summary.removedResourceRequirements || 0) > 0 && (
                  <span className="text-red-600 font-medium">-{summary.removedResourceRequirements}</span>
                )}
                {(summary.modifiedResourceRequirements || 0) > 0 && (
                  <span className="text-amber-600 font-medium">~{summary.modifiedResourceRequirements}</span>
                )}
              </div>
            )}
          </div>
          {expandedSections.has('resourceRequirements') && (
            <div className="p-4 space-y-3">
              {filteredResourceRequirementDeltas.map((delta, index) => (
                <div
                  key={delta.resourceRequirementUuid || `rr-${index}`}
                  className="border border-gray-200 rounded p-3"
                  style={{
                    backgroundColor: `${getChangeTypeColor(delta.changeType)}10`,
                    borderLeft: `4px solid ${getChangeTypeColor(delta.changeType)}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getChangeTypeIcon(delta.changeType)}</span>
                      <span className="font-semibold text-gray-900">
                        {delta.fromOperationName || '-'} → {delta.toOperationName || '-'}
                      </span>
                    </div>
                    <span
                      className="text-xs font-medium px-2 py-1 rounded"
                      style={{
                        backgroundColor: `${getChangeTypeColor(delta.changeType)}20`,
                        color: getChangeTypeColor(delta.changeType),
                      }}
                    >
                      {getChangeTypeLabel(delta.changeType)}
                    </span>
                  </div>
                  {delta.fieldChanges && Object.keys(delta.fieldChanges).length > 0 && (
                    <div className="ml-8 mt-2 space-y-1">
                      {Object.entries(delta.fieldChanges).map(([fieldName, change]) => {
                        if (showOnlyChanges && change.changeType === 'UNCHANGED') return null;
                        return (
                          <div key={fieldName} className="text-sm">
                            <span className="font-medium text-gray-700">
                              {t(`resourceRequirement.fields.${fieldName}`, fieldName)}:
                            </span>{' '}
                            {change.changeType === 'MODIFIED' ? (
                              <span>
                                <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                  {formatValue(change.oldValue)}
                                </span>
                                {' → '}
                                <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                  {formatValue(change.newValue)}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: getChangeTypeColor(change.changeType) }}>
                                {formatValue(change.newValue || change.oldValue)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {(onConfirm || onCancel) && (
        <div className="mt-6 flex justify-end gap-2">
          {onCancel && (
            <ButtonComponent
              type="button"
              onClick={onCancel}
              cssClass="e-outline"
            >
              {t('actions.cancel', 'Abbrechen')}
            </ButtonComponent>
          )}
          {onConfirm && (
            <ButtonComponent
              type="button"
              onClick={onConfirm}
              cssClass="e-primary"
            >
              {t('routing.import.confirm', 'Import bestätigen')}
            </ButtonComponent>
          )}
        </div>
      )}
    </div>
  );
};

export default RoutingDeltaView;


